import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import toast from "react-hot-toast";
// PaymentModal removed - subscriptions are paid upfront during checkout
import SubscriptionManagement from "./SubscriptionManagement";

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case "active":
        return "linear-gradient(45deg, #00b894, #00a085)";
      case "cancelled":
        return "linear-gradient(45deg, #e84393, #d63031)";
      case "completed":
        return "linear-gradient(45deg, #6c5ce7, #a29bfe)";
      default:
        return "linear-gradient(45deg, #74b9ff, #0984e3)";
    }
  }};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const VendorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 1rem;
  border: 3px solid #f8f9fa;
`;

const VendorInfo = styled.div`
  flex: 1;
`;

const VendorName = styled.h3`
  color: #2d3436;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
`;

const MealPlanTitle = styled.h4`
  color: #636e72;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
`;

const PlanType = styled.span`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const Details = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: #636e72;
  font-size: 0.9rem;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #2d3436;
  font-size: 0.9rem;
  font-weight: 600;
`;

const Price = styled.div`
  color: #00b894;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Address = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
`;

const AddressLabel = styled.div`
  color: #636e72;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const AddressText = styled.div`
  color: #2d3436;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  min-width: 120px;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: linear-gradient(45deg, #667eea, #764ba2);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
          }
        `;
      case "success":
        return `
          background: linear-gradient(45deg, #00b894, #00a085);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0, 184, 148, 0.4);
          }
        `;
      case "warning":
        return `
          background: linear-gradient(45deg, #fdcb6e, #e17055);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(253, 203, 110, 0.4);
          }
        `;
      case "danger":
        return `
          background: linear-gradient(45deg, #e84393, #d63031);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(232, 67, 147, 0.4);
          }
        `;
      default:
        return `
          background: #f8f9fa;
          color: #636e72;
          border: 1px solid #ddd;
          &:hover {
            background: #e9ecef;
          }
        `;
    }
  }}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  backdrop-filter: blur(5px);
`;

const SubscriptionCard = ({ subscription, onUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GY", {
      style: "currency",
      currency: "GYD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      await api.put(`/subscriptions/${subscription.id}/status`, {
        status: newStatus,
      });

      toast.success(`Subscription ${newStatus} successfully!`);
      onUpdate();
    } catch (error) {
      console.error("Error updating subscription status:", error);
      toast.error("Failed to update subscription status");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = () => {
    navigate(`/subscriptions/${subscription.id}`);
  };

  // Payment handling removed - subscriptions are paid upfront during checkout

  const getStatusActions = () => {
    const actions = [];

    // Note: Subscriptions are one-time purchases paid upfront, so no payment actions are needed
    // Payment is completed during checkout, so there's no "Pay Now" button

    // Add status-specific actions
    // Note: Subscriptions are one-time purchases paid upfront, so no cancel action is available
    // Status will automatically change to 'completed' when the plan ends
    switch (subscription.status) {
      case "active":
      case "cancelled":
      case "completed":
        // No actions available - subscriptions are one-time purchases
        break;
      default:
        break;
    }

    return actions;
  };

  return (
    <Card
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      {loading && (
        <LoadingOverlay>
          <div>Loading...</div>
        </LoadingOverlay>
      )}

      <StatusBadge status={subscription.status}>
        {subscription.status}
      </StatusBadge>

      <Header>
        <VendorImage
          src={subscription.vendor_image || "/api/placeholder/60/60"}
          alt={subscription.vendor_name}
        />
        <VendorInfo>
          <VendorName>{subscription.vendor_name}</VendorName>
          <MealPlanTitle>{subscription.meal_plan_title}</MealPlanTitle>
        </VendorInfo>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <PlanType>{subscription.plan_type}</PlanType>
          <SubscriptionManagement
            subscription={subscription}
            onUpdate={onUpdate}
            onDelete={(id) => {
              // Handle deletion - you might want to refresh the parent component
              onUpdate();
            }}
          />
        </div>
      </Header>

      <Details>
        <DetailRow>
          <DetailLabel>Start Date</DetailLabel>
          <DetailValue>{formatDate(subscription.start_date)}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>End Date</DetailLabel>
          <DetailValue>{formatDate(subscription.end_date)}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Payment Status</DetailLabel>
          <DetailValue
            style={{
              color: "#00b894", // Always green since payment is made upfront
            }}
          >
            Paid
          </DetailValue>
        </DetailRow>
      </Details>

      <Price>{formatCurrency(subscription.total_amount)}</Price>

      {subscription.address_line1 && (
        <Address>
          <AddressLabel>Delivery Address</AddressLabel>
          <AddressText>
            {subscription.address_line1}
            {subscription.address_line2 && `, ${subscription.address_line2}`}
            <br />
            {subscription.city}, {subscription.region}
          </AddressText>
        </Address>
      )}

      <Actions>
        <ActionButton
          variant="primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleViewDetails}
        >
          View Details
        </ActionButton>

        {getStatusActions().map((action, index) => (
          <ActionButton
            key={index}
            variant={action.variant}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={action.action}
          >
            {action.label}
          </ActionButton>
        ))}
      </Actions>

      {/* PaymentModal removed - subscriptions are paid upfront during checkout */}
    </Card>
  );
};

export default SubscriptionCard;
